// OnGarmentPreview (W4 phase 2): the live composited on-garment preview.
//
// Renders the customer's design on the ADMIN-CALIBRATED placement photo:
// the photo is the stage, and an SVG overlay whose viewBox is in
// photo-mm space (width = calibration.photoWidthMm, height derived from
// the photo's natural aspect ratio) draws the artwork inside the
// calibrated zone at its TRUE mm size. Mounted by App.tsx's preview dock
// INSTEAD of PreviewCanvas whenever the active placement has both
// imageUrl and calibration (presence of both = the feature flag);
// uncalibrated placements keep the artboard visualiser untouched.
//
// SIZE-IS-THE-NUMBER: the mm the customer picked stays the price +
// production authority. The photo only supplies the mm-per-px scale. If
// the picked size is bigger than the zone can show, the DISPLAY is
// scaled down (with a note) - sizeMm is never mutated from here.
//
// Drag repositions only (no resize gesture): pointerdown on the stage
// starts a drag, pointermove applies a rAF transform straight to the SVG
// group (no React re-render per move), pointerup/cancel commits the
// clamped offset to the draft via onPreviewOffsetChange. touch-action:
// none is scoped to the stage alone so the in-cart modal's scroll
// container (.rkc-cz__host) never fights the gesture.

import * as React from "react";
import type { Config, Placement } from "../types";
import type { EmbroideryDraft } from "../sections/PlacementSection";
import { usePreviewArtwork } from "../util/usePreviewArtwork";
import {
  CANVAS_MM_W,
  CANVAS_MM_H,
  computeLayout,
  LayoutArt,
  LayoutText,
  ArchedText,
  PreviewDefs,
  EmptyState,
  resolveGarmentHex,
  isDarkColour,
} from "./PreviewCanvas";
import {
  zoneRectMm,
  displayFitScale,
  clampOffsetMm,
  pxToMm,
  type OffsetMm,
} from "../util/previewGeometry";
import { emitAnalytics } from "../analytics";
import { flowKindFor } from "../flow";

// The layout block from computeLayout is centred on the artboard centre.
// Map it onto the photo: block centre -> (zone centre + offset), scaled
// by the display fit factor.
function artTransform(
  targetX: number,
  targetY: number,
  fit: number,
): string {
  const tx = targetX - fit * (CANVAS_MM_W / 2);
  const ty = targetY - fit * (CANVAS_MM_H / 2);
  return `translate(${tx} ${ty}) scale(${fit})`;
}

export function OnGarmentPreview({
  config,
  spot,
  placement,
  workerUrl,
  garmentColourHex,
  garmentColourName,
  active,
  onToggleRemoveBg,
  onPreviewOffsetChange,
}: {
  config: Config;
  /** The spot being previewed (live draft or most recent saved spot). */
  spot: EmbroideryDraft | null;
  /** The active placement. The PARENT gates photo mode on
   *  placement.imageUrl && placement.calibration, so both are present. */
  placement: Placement;
  workerUrl: string;
  garmentColourHex: string | null;
  garmentColourName: string | null;
  /** True while the preview drawer is open (heavy artwork work defers). */
  active: boolean;
  /** Same contract as PreviewCanvas: present only while a live draft is
   *  being edited. */
  onToggleRemoveBg?: (on: boolean) => void;
  /** Commit a drag reposition to the draft. Present only while a live
   *  draft is being edited; absent = the preview is read-only. */
  onPreviewOffsetChange?: (offset: OffsetMm) => void;
}) {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const artGroupRef = React.useRef<SVGGElement>(null);

  // The customer's CHOSEN garment colour still drives the drawing
  // filter's default line contrast, exactly like the artboard preview.
  const garmentHex = resolveGarmentHex(garmentColourHex, garmentColourName);
  const dark = isDarkColour(garmentHex);

  const art = usePreviewArtwork({ config, spot, workerUrl, active, dark });

  // Natural aspect of the placement photo. The mm overlay can't render
  // until this is known (frame height = photoWidthMm / aspect).
  const [photoAspect, setPhotoAspect] = React.useState<number | null>(null);
  const [photoFailed, setPhotoFailed] = React.useState(false);
  React.useEffect(() => {
    // New placement photo = fresh measurement.
    setPhotoAspect(null);
    setPhotoFailed(false);
  }, [placement.imageUrl]);

  const calibration = placement.calibration ?? null;
  const zone =
    calibration && photoAspect ? zoneRectMm(calibration, photoAspect) : null;
  const frameWMm = calibration?.photoWidthMm ?? 0;
  const frameHMm =
    photoAspect && photoAspect > 0 && frameWMm > 0
      ? frameWMm / photoAspect
      : 0;

  // Same layout maths as the artboard preview - true mm, no zone-shape
  // special cases (the calibrated zone IS this placement's envelope).
  const layout = art.isEmpty
    ? null
    : computeLayout({
        hasText: art.hasText,
        hasArt: art.hasArt,
        isPdf: art.isPdf,
        imgAspect: art.imgAspect,
        sizeMm: art.sizeMm,
        arrangement: art.isVarsity ? "above" : spot?.arrangement ?? null,
        textLen: art.displayText.length,
        varsity: art.isVarsity,
      });

  // DISPLAY-ONLY fit clamp. fit < 1 = the picked mm overflows the zone;
  // the preview shrinks it and says so, the stitched size stays sizeMm.
  const fit =
    layout && zone ? displayFitScale(layout.blockW, layout.blockH, zone) : 1;
  const shownW = layout ? layout.blockW * fit : 0;
  const shownH = layout ? layout.blockH * fit : 0;

  // The committed offset, re-clamped at render time so a size change
  // that outgrows the old position never draws outside the zone (the
  // STORED offset is left alone - display only).
  const storedOffset: OffsetMm = spot?.previewOffset ?? { xMm: 0, yMm: 0 };
  const shownOffset = zone
    ? clampOffsetMm(storedOffset, shownW, shownH, zone)
    : { xMm: 0, yMm: 0 };

  // ── Drag (reposition only) ───────────────────────────────────────────
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    baseOffset: OffsetMm;
    stagePxW: number;
    live: OffsetMm;
    raf: number;
    moved: boolean;
  } | null>(null);
  // rk_preview_dragged fires once per spot (keyed by placement slug - one
  // draft per placement at a time) on the first real move.
  const draggedSlugsRef = React.useRef<Set<string>>(new Set());

  const draggable =
    !!onPreviewOffsetChange && !!layout && !!zone && !art.isEmpty;

  const applyLiveTransform = React.useCallback(() => {
    const d = dragRef.current;
    const g = artGroupRef.current;
    if (!d || !g || !zone) return;
    d.raf = 0;
    g.setAttribute(
      "transform",
      artTransform(zone.cx + d.live.xMm, zone.cy + d.live.yMm, fit),
    );
  }, [zone, fit]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable || !zone) return;
    if (dragRef.current) return; // one pointer drives the drag
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    if (rect.width <= 0) return;
    try {
      stage.setPointerCapture(e.pointerId);
    } catch {
      // capture is best-effort; the move/up handlers still work
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      baseOffset: shownOffset,
      stagePxW: rect.width,
      live: shownOffset,
      raf: 0,
      moved: false,
    };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId || !zone) return;
    const dxMm = pxToMm(e.clientX - d.startClientX, frameWMm, d.stagePxW);
    const dyMm = pxToMm(e.clientY - d.startClientY, frameWMm, d.stagePxW);
    d.live = clampOffsetMm(
      { xMm: d.baseOffset.xMm + dxMm, yMm: d.baseOffset.yMm + dyMm },
      shownW,
      shownH,
      zone,
    );
    if (!d.moved && (dxMm !== 0 || dyMm !== 0)) {
      d.moved = true;
      const slug = placement.slug;
      if (!draggedSlugsRef.current.has(slug)) {
        draggedSlugsRef.current.add(slug);
        emitAnalytics("preview_dragged", {
          productGid: config.product.productGid,
          productHandle: config.product.productHandle,
          placementSlug: slug,
          flowKind: flowKindFor(config),
        });
      }
    }
    // rAF-coalesced direct attribute write - no React render per move.
    if (!d.raf) d.raf = requestAnimationFrame(applyLiveTransform);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (d.raf) cancelAnimationFrame(d.raf);
    dragRef.current = null;
    setDragging(false);
    try {
      stageRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    if (d.moved && onPreviewOffsetChange) {
      onPreviewOffsetChange(d.live);
    }
  };

  // Abandon cleanly if the component unmounts mid-drag.
  React.useEffect(
    () => () => {
      const d = dragRef.current;
      if (d?.raf) cancelAnimationFrame(d.raf);
      dragRef.current = null;
    },
    [],
  );

  // ── Analytics: photo mode shown, once per placement per mount ────────
  const shownSlugsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!active) return;
    const slug = placement.slug;
    if (shownSlugsRef.current.has(slug)) return;
    shownSlugsRef.current.add(slug);
    emitAnalytics("preview_photo_shown", {
      productGid: config.product.productGid,
      productHandle: config.product.productHandle,
      placementSlug: slug,
      flowKind: flowKindFor(config),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, placement.slug]);

  // While a drag is live, the group's transform is owned by the rAF
  // writer; any incidental re-render must not snap it back to the stale
  // committed offset.
  const renderOffset = dragging && dragRef.current ? dragRef.current.live : shownOffset;
  const groupTransform = zone
    ? artTransform(zone.cx + renderOffset.xMm, zone.cy + renderOffset.yMm, fit)
    : undefined;

  const overflowNote =
    layout && zone && fit < 1
      ? `Bigger than this photo can show, so it is scaled down here. It will stitch at the ${art.sizeMm}mm you picked.`
      : null;

  return (
    <div className="rk-pc-pv-canvas rk-pc-pv-canvas--photo">
      <div className="rk-pc-pv-meta">
        <span className="rk-pc-pv-meta__eyebrow">Live preview</span>
        <span className="rk-pc-pv-meta__placement">
          <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
            <circle cx="5" cy="5" r="3.6" fill="currentColor" />
          </svg>
          {placement.name}
        </span>
        <span className="rk-pc-pv-meta__spacer" />
        <span className="rk-pc-pv-meta__size">
          <strong>{spot?.sizeMm || 0}</strong>
          <span>mm</span>
        </span>
      </div>

      <div
        className={[
          "rk-pc-pv-photo",
          draggable ? "is-draggable" : "",
          dragging ? "is-dragging" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        ref={stageRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          className="rk-pc-pv-photo__img"
          src={placement.imageUrl ?? undefined}
          alt={`${placement.name} placement photo`}
          draggable={false}
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth > 0 && im.naturalHeight > 0) {
              setPhotoAspect(im.naturalWidth / im.naturalHeight);
            }
          }}
          onError={() => setPhotoFailed(true)}
        />

        {zone && frameHMm > 0 ? (
          <svg
            className="rk-pc-pv-photo__svg"
            viewBox={`0 0 ${frameWMm} ${frameHMm}`}
            preserveAspectRatio="none"
            aria-hidden={art.isEmpty}
          >
            <PreviewDefs />
            {dragging ? (
              <rect
                className="rk-pc-pv-photo__zone"
                x={zone.x}
                y={zone.y}
                width={zone.w}
                height={zone.h}
                rx={2}
              />
            ) : null}
            {layout ? (
              <g ref={artGroupRef} transform={groupTransform}>
                {layout.art ? (
                  <LayoutArt
                    box={layout.art}
                    imageUrl={art.finalArtUrl}
                    isPdf={art.isPdf}
                    filename={spot?.uploadFilename ?? null}
                  />
                ) : null}
                {layout.text ? (
                  art.isVarsity ? (
                    <ArchedText
                      box={layout.text}
                      text={art.displayText}
                      font={art.font}
                      colour={art.textColour}
                      strokeColour={art.outlineColour}
                    />
                  ) : (
                    <LayoutText
                      box={layout.text}
                      text={art.displayText}
                      font={art.font}
                      colour={art.textColour}
                    />
                  )
                ) : null}
              </g>
            ) : null}
          </svg>
        ) : null}

        {art.isEmpty && !photoFailed ? <EmptyState /> : null}

        {photoFailed ? (
          <div className="rk-pc-pv-photo__failed" role="status">
            Photo unavailable right now. Your design and sizes are unaffected.
          </div>
        ) : null}

        {art.bgBusy ? (
          <div className="rk-pc-pv-working" aria-hidden="true">
            <span className="rk-pc-pv-working__spinner" />
            Removing background… (give it a second)
          </div>
        ) : null}

        {dragging && layout ? (
          <div className="rk-pc-pv-photo__badge" aria-hidden="true">
            <strong>{Math.round(layout.blockW)}</strong> x{" "}
            <strong>{Math.round(layout.blockH)}</strong> mm
          </div>
        ) : null}
      </div>

      {draggable ? (
        <div className="rk-pc-pv-note">
          Drag the design to position it. Size stays exactly what you picked.
        </div>
      ) : null}
      {overflowNote ? <div className="rk-pc-pv-note">{overflowNote}</div> : null}
      {art.drawingNote ? (
        <div className="rk-pc-pv-note">{art.drawingNote}</div>
      ) : null}

      {/* Remove-background toggle: same contract + gating as the
          artboard preview (visual-only aid, non-PDF uploads, live draft
          only). */}
      {onToggleRemoveBg &&
        art.imageUrl &&
        !(spot?.uploadMimeType ?? "").toLowerCase().includes("pdf") &&
        !(spot?.uploadFilename ?? "").toLowerCase().endsWith(".pdf") && (
          <label className="rk-pc-pv-bgtoggle">
            <input
              type="checkbox"
              checked={art.removeBg}
              onChange={(e) => onToggleRemoveBg(e.target.checked)}
            />
            <span className="rk-pc-pv-bgtoggle__label">
              Remove background
              {art.removeBg && art.bgBusy && (
                <span className="rk-pc-pv-bgtoggle__busy"> processing…</span>
              )}
            </span>
          </label>
        )}

      <div className="rk-pc-pv-legend">
        <span className="rk-pc-pv-legend__item rk-pc-pv-legend__item--muted">
          A visual guide, not 100% accurate. The photo shows position and
          scale, the stitched size is the mm you picked.
        </span>
      </div>
    </div>
  );
}
